import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useMappedState } from "redux-react-hook";
import {
  Dropdown,
  DropdownItem,
  KebabToggle,
  Modal,
  ModalVariant,
  Button,
  Text,
  Grid,
  GridItem,
  FormSelect,
  TextInput,
} from "@patternfly/react-core";
import arrayToFormSelectOptions from "../../utils/general";
import {
  REQUESTED_PRODUCTS,
  REQUESTED_UPDATE_PRODUCT_PHASES,
} from "../../redux/actions/productAction";
// import { REQUESTED_ACTIVITIES } from "../../redux/actions/activityAction";

export default (props) => {
  const { phaseList, isModalOpen, handleModalToggle } = props;
  const phaseOptions = arrayToFormSelectOptions(phaseList);
  const [phases, setPhases] = useState(props.phases);
  const [isOpen, setIsOpen] = useState(phases.map(() => false));

  // Devided all phases related updates into 4 categories:
  // add: to add a new phase
  // update: to update existing phase (weight only to be more specific)
  // rename: update existing phase's name
  // delete: remove phase
  const [phaseUpdates, setPhaseUpdates] = useState({
    add: [],
    update: [],
    rename: [],
    delete: [],
  });

  const dispatch = useDispatch();
  const mapState = useCallback(
    (state) => ({
      isUpdatingProductPhases: state.productState.isUpdatingProductPhases,
      isUpdatedProductPhases: state.productState.isUpdatedProductPhases,
      productsMap: state.productState.productsMap,
    }),
    []
  );

  const {
    isUpdatingProductPhases,
    isUpdatedProductPhases,
    productsMap,
  } = useMappedState(mapState);

  const onChangeFormSelect = useCallback(
    (index, value) => {
      // set up the array to rename phases
      let newPhaseUpdates = { ...phaseUpdates };
      // find the phase from props
      let prevPhase = props.phases.find(
        (phase) => phase.id === phases[index].id
      );
      // find the new phase id by its name
      let newId = phaseList.find((phase) => phase.name === value)
        ? phaseList.find((phase) => phase.name === value).id
        : null;

      if (prevPhase) {
        // update existing phase
        // find if there is the phase is already in phaseUpdates.rename array
        let itemIndex = phaseUpdates.rename.findIndex(
          (item) => item.id === prevPhase.id
        );

        // if new id is valid (exclude the senario that user change the phase name to 'Please Choose ...')
        if (newId) {
          if (value !== prevPhase.name) {
            // if the phase name is changed, check if the change is already in the rename array
            // if yes, update the values
            // if no, push it into the array
            if (itemIndex !== -1) {
              newPhaseUpdates.rename[itemIndex].prevId = prevPhase.id;
              newPhaseUpdates.rename[itemIndex].newId = newId;
            } else {
              newPhaseUpdates.rename.push({
                prevId: prevPhase.id,
                newId,
              });
            }
          } else {
            // if the phase name is not changed comparing with props value
            // if the changes is in the rename array, we need to remove it
            if (itemIndex !== -1) {
              newPhaseUpdates.rename.splice(itemIndex, 1);
            }
          }
        }
        // if new id is invalid (user change to 'Please Choose ...')
        // need to check the rename array to remove changes
        else {
          if (itemIndex !== -1) {
            newPhaseUpdates.rename.splice(itemIndex, 1);
          }
        }
      } else {
        // add new phase
        // find if there is the phase is already in phaseUpdates.add array
        let itemIndex = phaseUpdates.add.findIndex(
          (item) => item.gid === phases[index].id
        );

        if (newId) {
          if (itemIndex !== -1) {
            newPhaseUpdates.add[itemIndex].id = newId;
          } else {
            newPhaseUpdates.add.push({
              gid: phases[index].id,
              id: newId,
            });
          }
        } else {
          if (itemIndex !== -1) {
            newPhaseUpdates.add.splice(itemIndex, 1);
          }
        }
      }
      setPhaseUpdates(newPhaseUpdates);

      let newPhases = [...phases];
      newPhases[index].name = value;
      setPhases(newPhases);
    },
    [phases, props.phases, phaseUpdates, phaseList]
  );

  const onChangeTextInput = useCallback(
    (index, value) => {
      let newPhaseUpdates = { ...phaseUpdates };
      let prevPhase = props.phases.find(
        (phase) => phase.id === phases[index].id
      );
      value = parseInt(value) || 0;

      if (prevPhase) {
        let itemIndex = prevPhase
          ? phaseUpdates.update.findIndex((item) => item.id === prevPhase.id)
          : -1;
        if (value !== prevPhase.weight) {
          if (itemIndex !== -1) {
            newPhaseUpdates.update[itemIndex].weight = value;
          } else {
            newPhaseUpdates.update.push({
              id: prevPhase.id,
              weight: value,
            });
          }
        } else {
          if (itemIndex !== -1) {
            newPhaseUpdates.update.splice(itemIndex, 1);
          }
        }
      } else {
        let itemIndex = phaseUpdates.add.findIndex(
          (item) => item.gid === phases[index].id
        );

        if (itemIndex !== -1) {
          newPhaseUpdates.add[itemIndex].weight = value;
        } else {
          newPhaseUpdates.add.push({
            gid: phases[index].id,
            weight: value,
          });
        }
      }
      setPhaseUpdates(newPhaseUpdates);

      let newPhases = [...phases];
      newPhases[index].weight = value;
      setPhases(newPhases);
    },
    [phases, props.phases, phaseUpdates]
  );

  const onSelectDropDown = useCallback(
    (index, event) => {
      let newIsOpen = [...isOpen];
      newIsOpen[index] = !isOpen[index];
      setIsOpen(newIsOpen);

      let newPhases = [...phases];
      let newPhaseUpdates = { ...phaseUpdates };

      // delete phases
      newPhaseUpdates.delete.push(newPhases[index]);
      setPhaseUpdates(newPhaseUpdates);

      newPhases.splice(index, 1);
      setPhases(newPhases);
    },
    [phases, phaseUpdates, isOpen]
  );

  const onDropDownToggle = useCallback(
    (index) => {
      let newIsOpen = [...isOpen];
      newIsOpen[index] = !isOpen[index];
      setIsOpen(newIsOpen);
    },
    [isOpen]
  );

  function addNewPhase() {
    let newPhases = [...phases];
    newPhases.push({ id: phases.length + 1, name: "" });
    setPhases(newPhases);
  }

  function onSave() {
    // save the changes of phases
    if (!isUpdatingProductPhases) {
      dispatch({
        type: REQUESTED_UPDATE_PRODUCT_PHASES,
        payload: { id: props.productId, phases: phaseUpdates },
      });
    }

    //handleModalToggle();
  }

  function onClose() {
    setPhases(props.phases);
    handleModalToggle();
  }

  const renderPhaseList = useCallback(() => {
    return phases.map((phase, index) => (
      <React.Fragment key={`gridItem-${index}`}>
        <GridItem span={1} key={`gridItem-order-${index}`}>
          <Text id={`index-${index}`}>{index + 1}</Text>
        </GridItem>
        <GridItem span={1} key={`gridItem-weight-${index}`}>
          <TextInput
            type="text"
            id={`weight-${index}`}
            onChange={(value) => onChangeTextInput(index, value)}
            value={phase.weight}
          />
        </GridItem>
        <GridItem span={6} key={`gridItem-name-${index}`}>
          <FormSelect
            value={phase.name}
            onChange={(value) => onChangeFormSelect(index, value)}
            aria-label={`phase-name-select-${index}`}
          >
            {phaseOptions}
          </FormSelect>
        </GridItem>
        <GridItem span={2} key={`gridItem-placeholder-${index}`}></GridItem>
        <GridItem span={2} key={`gridItem-dropdown-${index}`}>
          <Dropdown
            isPlain
            onSelect={() => onSelectDropDown(index)}
            toggle={<KebabToggle onToggle={() => onDropDownToggle(index)} />}
            isOpen={isOpen[index]}
            dropdownItems={[
              <DropdownItem key="opt-1" value="Option 1" component="button">
                Delete this phase
              </DropdownItem>,
            ]}
          />
        </GridItem>
      </React.Fragment>
    ));
  }, [
    phases,
    phaseOptions,
    isOpen,
    onChangeFormSelect,
    onSelectDropDown,
    onDropDownToggle,
    onChangeTextInput,
  ]);

  useEffect(() => {
    renderPhaseList();
  }, [phases, renderPhaseList]);

  useEffect(() => {
    if (isUpdatedProductPhases && isModalOpen) {
      dispatch({
        type: REQUESTED_PRODUCTS,
        payload: {},
      });
      // dispatch({
      //   type: REQUESTED_ACTIVITIES,
      //   payload: {},
      // });
      handleModalToggle();
    }
  }, [
    dispatch,
    isUpdatedProductPhases,
    productsMap,
    props.productId,
    isModalOpen,
    handleModalToggle,
  ]);

  return (
    <Modal
      variant={ModalVariant.large}
      title="Edit Phase"
      isOpen={isModalOpen}
      onClose={() => onClose()}
      actions={[
        <Button key="Add" variant="primary" onClick={() => addNewPhase()}>
          Add New Phase
        </Button>,
        <Button key="Save" variant="primary" onClick={() => onSave()}>
          Save
        </Button>,
        <Button key="Cancel" variant="secondary" onClick={() => onClose()}>
          Cancel
        </Button>,
      ]}
    >
      <Grid hasGutter style={{ paddingTop: "1rem", paddingBottom: "4rem" }}>
        <GridItem span={1} key={`gridItem-order`}></GridItem>
        <GridItem span={1} key={`gridItem-weight`}>
          <Text>Weight</Text>
        </GridItem>
        <GridItem span={6} key={`gridItem-name`}>
          <Text>Phase Name</Text>
        </GridItem>
        <GridItem span={2} key={`gridItem-placeholder`}></GridItem>
        <GridItem span={2} key={`gridItem-action`}>
          <Text>Actions</Text>
        </GridItem>
        {renderPhaseList()}
      </Grid>
    </Modal>
  );
};
